package com.appsyde.shared.modules.account.data.source

import com.appsyde.shared.modules.account.data.AccountResponseDto
import com.appsyde.shared.modules.account.data.BillingStatusDto
import com.appsyde.shared.modules.account.data.CheckoutSessionResponseDto
import com.appsyde.shared.modules.account.data.CustomerPortalResponseDto
import com.appsyde.shared.modules.account.data.MeResponseDto
import com.appsyde.shared.modules.account.data.SignatureUpdateRequestDto
import com.appsyde.shared.modules.account.data.SignatureUpdateResponseDto
import com.appsyde.shared.modules.account.data.ToneUpdateRequestDto
import com.appsyde.shared.modules.account.data.ToneUpdateResponseDto
import com.appsyde.shared.network.exception.NotAuthenticatedException
import com.appsyde.shared.network.requireSuccessOrBody
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.accept
import io.ktor.client.request.get
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.HttpStatusCode
import kotlinx.serialization.json.Json

class AccountRemoteDataSourceImpl(
    private val http: HttpClient,
    private val baseUrl: String,
) : AccountRemoteDataSource {

    private val accountUrl = baseUrl.trimEnd('/') + "/account"
    private val meUrl = baseUrl.trimEnd('/') + "/me"
    private val toneUrl = "$accountUrl/tone"
    private val signatureUrl = "$accountUrl/signature"
    private val requestDeletionUrl = "$accountUrl/request-deletion"
    private val onboardingCompleteUrl = baseUrl.trimEnd('/') + "/onboarding/complete"
    private val billingStatusUrl = baseUrl.trimEnd('/') + "/billing/status"
    private val checkoutSessionUrl = baseUrl.trimEnd('/') + "/billing/checkout-session"
    private val customerPortalUrl = baseUrl.trimEnd('/') + "/billing/customer-portal"

    override suspend fun getMe(): MeResponseDto? = runCatching {
        val response = http.get(meUrl) {
            accept(ContentType.Application.Json)
        }
        val raw = response.requireSuccessOrBody("me_failed")
        Json { ignoreUnknownKeys = true }.decodeFromString<MeResponseDto>(raw)
    }.getOrElse { e ->
        if (e is NotAuthenticatedException) throw e
        null
    }

    override suspend fun getAccount(): AccountResponseDto? = runCatching {
        http.get(accountUrl) {
            accept(ContentType.Application.Json)
        }.body<AccountResponseDto>()
    }.getOrNull()

    override suspend fun updateTone(tone: String): String? = runCatching {
        http.patch(toneUrl) {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)
            setBody(ToneUpdateRequestDto(tone = tone))
        }.body<ToneUpdateResponseDto>().tone
    }.getOrNull()

    override suspend fun updateSignature(signatureEnabled: Boolean, signatureValue: String): SignatureUpdateResponseDto? = runCatching {
        http.patch(signatureUrl) {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)
            setBody(SignatureUpdateRequestDto(signatureEnabled = signatureEnabled, signatureValue = signatureValue))
        }.body<SignatureUpdateResponseDto>()
    }.getOrNull()

    override suspend fun requestAccountDeletion(): RequestDeletionResultDto = runCatching {
        val response = http.post(requestDeletionUrl) {
            accept(ContentType.Application.Json)
        }
        when (response.status) {
            HttpStatusCode.OK -> RequestDeletionResultDto.Ok
            HttpStatusCode.Conflict -> RequestDeletionResultDto.AlreadyPending
            else -> RequestDeletionResultDto.Error(response.status.description)
        }
    }.getOrElse { e -> RequestDeletionResultDto.Error(e.message) }

    override suspend fun completeOnboarding(): Boolean = runCatching {
        val response = http.post(onboardingCompleteUrl) {
            accept(ContentType.Application.Json)
        }
        response.status == HttpStatusCode.OK
    }.getOrElse { false }

    override suspend fun getBillingStatus(): BillingStatusDto? = runCatching {
        http.get(billingStatusUrl) {
            accept(ContentType.Application.Json)
        }.body<BillingStatusDto>()
    }.getOrNull()

    override suspend fun createCheckoutSession(): CheckoutSessionResponseDto? = runCatching {
        http.post(checkoutSessionUrl) {
            accept(ContentType.Application.Json)
        }.body<CheckoutSessionResponseDto>()
    }.getOrNull()

    override suspend fun createCustomerPortalSession(): CustomerPortalResponseDto? = runCatching {
        http.post(customerPortalUrl) {
            accept(ContentType.Application.Json)
        }.body<CustomerPortalResponseDto>()
    }.getOrNull()
}
